import { FormEvent, useEffect, forwardRef, useMemo } from 'react';
import { cx } from '@linaria/atomic';
import { isFunction } from 'lodash-es';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { usePreviousValue } from '../../hooks/use-previous-value';
import { CaretLeftIcon } from '../../icon/icons/caret-left';
import { CaretRightIcon } from '../../icon/icons/caret-right';
import { DropdownField } from '../forms/fields/dropdown/dropdown-field.component';
import { NumberField } from '../forms/fields/number-field/number-field.component';
import { useFormField } from '../forms/hooks/use-form-field/use-form-field';
import { IconButton } from '../icon-button/icon-button.component';
import { defaultRowsPerPageOptions } from '../table/table.component';
import { Text } from '../text/text.component';
import { PaginationButton } from './pagination-button.component';
import {
  paginationPageByPageContainerStyle,
  paginationPageByPageRowStyle,
  paginationPageByPageTextStyle,
  paginationPageByPageTotalTextStyle,
  paginationContainerBaseStyle,
  paginationContainerSpaceBetweenStyle,
  paginationContainerFlexEndStyle,
  paginationControlsStyle,
  paginationPageLabelStyle,
  paginationGoToPageFieldStyle,
  paginationPageSelectorContainerStyle,
  paginationPageSelectorLabelStyle,
  paginationNumFieldStyle,
  paginationInlineFlexStyle,
  paginationNextButtonMarginStyle,
  paginationFormStyle,
  paginationErrorTextStyle,
} from './pagination.styles';
import { usePagination } from './use-pagination';

export type RowsPerPageOption = number[];

export type PaginationProps = {
  boundaries?: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
  nextHoverText?: string;
  page: number;
  siblings?: number;
  prevHoverText?: string;
  showGoToPageField?: boolean;
  showNumRowSelector?: boolean;
  goToPageLabel?: string;
  rowsPerPageLabel?: string;
  pageChangeLabel?: string;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: RowsPerPageOption;
  disablePagination?: boolean;
  pageByPageSelector?: boolean;
  onNumRowsChange?: (num: number) => void;
  globalTrackingId?: string;
} & HasNumOfPagesProps;

type HasNumOfPagesProps =
  | {
      total?: undefined;
      handleChange: (prevOrNext: 'next' | 'prev') => void;
    }
  | {
      total: number;
      handleChange: (selectedPage: number) => void;
    };

const trackingIds = {
  goToPage: 'num-paginationgotopage',
  pageSelector: 'drp-paginationpageselector',
  nextPage: 'btn-paginationnextpage',
  previousPage: 'btn-paginationpreviouspage',
  pageNumber: 'btn-paginationpagenumber',
} as const;

type TrackingIds = typeof trackingIds;
type PaginationTrackingIds = { -readonly [K in keyof TrackingIds]: TrackingIds[K] | undefined };
export type PaginationTrackingId = Readonly<PaginationTrackingIds>;

const paginationTrackingIdGenerator = (globalId?: string) => {
  const delimiter = globalId?.endsWith('-') ? '' : '-';

  const trackIds = Object.keys(trackingIds).reduce((acc, key) => {
    const trackingKey = key;
    acc[trackingKey] = globalId ? `${globalId}${delimiter}${trackingIds[trackingKey]}` : undefined;
    return acc;
  }, {} as PaginationTrackingIds);
  return trackIds;
};

// eslint-disable-next-line react/display-name
export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      boundaries = 3,
      handleChange,
      hasNext = false,
      hasPrevious = false,
      nextHoverText = 'Next',
      page,
      prevHoverText = 'Previous',
      showGoToPageField = true,
      siblings = 1,
      total,
      onNumRowsChange,
      showNumRowSelector = true,
      defaultRowsPerPage,
      goToPageLabel = 'Go to page',
      rowsPerPageLabel = 'Rows per page',
      pageChangeLabel,
      rowsPerPageOptions,
      disablePagination,
      pageByPageSelector,
      globalTrackingId,
      ...rest
    }: PaginationProps,
    ref
  ) => {
    const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
    const hasEndRangeValue = !!total && !isMobile;
    const showForm = hasEndRangeValue && showGoToPageField;
    const paginationTrackingIds = useMemo(() => paginationTrackingIdGenerator(globalTrackingId), [globalTrackingId]);

    const jumpToPageFieldProps = useFormField(
      {
        allowDecimal: false,
        max: total,
        min: 1,
        type: 'number',
        value: page.toString(),
        validateOnChange: pageByPageSelector ? true : false,
      },
      [page]
    );

    const { range, setPage, next, previous, active, skipBackward, skipForward } = usePagination({
      page,
      siblings,
      total: total! > 0 ? total : undefined,
      onChange: (pageNumber) => {
        if (total !== undefined) {
          handleChange(pageNumber);
        } else {
          const prevOrNext = pageNumber > active! ? 'next' : 'prev';
          handleChange(prevOrNext);
        }
      },
      initialPage: page,
      boundaries,
    });

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault();
      if (total) {
        if (parseInt(jumpToPageFieldProps.value) > total!) {
          return;
        }
        handleChange(parseInt(jumpToPageFieldProps.value));
      }
    };

    return pageByPageSelector ? (
      <div ref={ref} className={paginationPageByPageContainerStyle} {...rest}>
        <div className={paginationPageByPageRowStyle}>
          <IconButton
            disabled={page === 1}
            label={prevHoverText}
            onClick={previous}
            showLabelOnHover={hasEndRangeValue}
            className={paginationInlineFlexStyle}
          >
            <CaretLeftIcon />
          </IconButton>
          <Text className={paginationPageByPageTextStyle}>{pageChangeLabel ?? 'Page'}</Text>
          <form onSubmit={handleSubmit} className={paginationFormStyle}>
            <NumberField
              disabled={range.length <= 1 || disablePagination}
              name='page-jump'
              label=''
              {...jumpToPageFieldProps}
              helperText='OKK'
              hideErrorText
              className={paginationNumFieldStyle}
            />
          </form>
          {total && (
            <Text className={paginationPageByPageTotalTextStyle}>
              {'of'} {total}
            </Text>
          )}
          <IconButton
            disabled={active === total}
            label={nextHoverText}
            onClick={next}
            showLabelOnHover={hasEndRangeValue}
            className={cx(paginationInlineFlexStyle, hasEndRangeValue && paginationNextButtonMarginStyle)}
          >
            <CaretRightIcon />
          </IconButton>
        </div>
        <Text size='small' color='error' className={paginationErrorTextStyle}>
          {jumpToPageFieldProps.error}
        </Text>
      </div>
    ) : (
      <div
        ref={ref}
        className={cx(
          paginationContainerBaseStyle,
          showNumRowSelector ? paginationContainerSpaceBetweenStyle : paginationContainerFlexEndStyle
        )}
      >
        {showNumRowSelector && (
          <PageSelector
            isMobile={isMobile}
            trackingId={paginationTrackingIds.pageSelector}
            defaultRowsPerPage={defaultRowsPerPage}
            onSelectionChanged={onNumRowsChange}
            rowsPerPageLabel={rowsPerPageLabel}
            rowsPerPageOptions={rowsPerPageOptions}
            disablePagination={disablePagination}
          />
        )}
        <div className={paginationControlsStyle} {...rest}>
          {/* IconButtons always render, but get displayed differently based on component configuration */}
          {!hasEndRangeValue && (
            <Text className={paginationPageLabelStyle}>
              {pageChangeLabel ?? 'Page'} {page}
            </Text>
          )}
          <IconButton
            disabled={page === 1 || !hasPrevious}
            label={prevHoverText}
            onClick={previous}
            showLabelOnHover={hasEndRangeValue}
            trackingId={paginationTrackingIds.previousPage}
            className={paginationInlineFlexStyle}
          >
            <CaretLeftIcon />
          </IconButton>
          {/* Only show the range if the end value is known */}
          {hasEndRangeValue && (
            <>
              {range.map((rangeValue, index) => (
                <PaginationButton
                  trackingId={paginationTrackingIds.pageNumber}
                  key={index}
                  page={page}
                  rangeValue={rangeValue}
                  setPage={setPage}
                  shouldShowSkipToEnd={index > range.length / 2 && rangeValue === 'dots'}
                  shouldShowSkipToStart={index < range.length / 2 && rangeValue === 'dots'}
                  skipBackward={skipBackward}
                  skipForward={skipForward}
                />
              ))}
            </>
          )}
          {/* IconButtons always render, but get displayed differently based on component configuration */}
          <IconButton
            disabled={active === total || !hasNext}
            label={nextHoverText}
            onClick={next}
            showLabelOnHover={hasEndRangeValue}
            trackingId={paginationTrackingIds.nextPage}
            className={cx(paginationInlineFlexStyle, hasEndRangeValue && paginationNextButtonMarginStyle)}
          >
            <CaretRightIcon />
          </IconButton>
          {showForm && (
            <form onSubmit={handleSubmit} className={paginationFormStyle}>
              <NumberField
                disabled={range.length <= 1 || disablePagination}
                name='page-jump'
                data-trackingid={paginationTrackingIds.goToPage}
                label={goToPageLabel}
                {...jumpToPageFieldProps}
                className={paginationGoToPageFieldStyle}
              />
            </form>
          )}
        </div>
      </div>
    );
  }
);

type PageSelectorProps = {
  defaultRowsPerPage?: number;
  onSelectionChanged?: (num: number) => void;
  rowsPerPageLabel: string;
  rowsPerPageOptions?: RowsPerPageOption;
  disablePagination?: boolean;
  isMobile?: boolean;
  trackingId?: string;
};

const PageSelector = ({
  defaultRowsPerPage,
  onSelectionChanged,
  rowsPerPageLabel,
  rowsPerPageOptions = defaultRowsPerPageOptions,
  disablePagination,
  trackingId,
  isMobile,
}: PageSelectorProps) => {
  const dropdownProps = useFormField({
    type: 'dropdown',
    value: defaultRowsPerPage?.toString() ?? rowsPerPageOptions[0]?.toString(),
  });
  const [previousValue, currentValue] = usePreviousValue(dropdownProps.value);

  useEffect(() => {
    if (previousValue !== currentValue && isFunction(onSelectionChanged)) {
      onSelectionChanged(+dropdownProps.value as number);
    }
  }, [dropdownProps.value]);

  return (
    <section className={paginationPageSelectorContainerStyle}>
      {!isMobile && <Text className={paginationPageSelectorLabelStyle}>{rowsPerPageLabel}</Text>}
      <DropdownField
        data-trackingid={trackingId}
        disabled={!!disablePagination}
        {...dropdownProps}
        label=''
        name='rows-per-page'
      >
        {[...rowsPerPageOptions].reverse().map((option) => (
          <DropdownField.Option
            key={option}
            value={String(option)}
            {...(trackingId && { trackingId: `${trackingId}-option-${option}` })}
          >
            {option}
          </DropdownField.Option>
        ))}
      </DropdownField>
    </section>
  );
};
